import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea, ScrollBar } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  BarChart3,
  Clock,
  CreditCard,
  AlertCircle,
  FileText,
  CheckCircle2,
  Download,
  Users,
  RefreshCw,
  List,
  Maximize2,
  Minimize2,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Search,
  User,
  Send,
  FolderOpen,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsPage = ({ user, settings }) => {
  const [activeTab, setActiveTab] = useState("full-list");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientStatus, setClientStatus] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Search and sort
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("code_client");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    fetchReport(activeTab);
  }, [activeTab]);

  useEffect(() => {
    filterAndSortData();
  }, [data, searchTerm, sortField, sortOrder]);

  const fetchReport = async (type) => {
    setLoading(true);
    try {
      const endpoint = type.replace("-", "-");
      const response = await axios.get(`${API}/reports/${endpoint}`, {
        withCredentials: true,
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Erreur lors du chargement du rapport");
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortData = () => {
    let result = [...data];

    // Filter
    if (searchTerm && activeTab === "full-list") {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.code_client?.toLowerCase().includes(search) ||
          item.nom?.toLowerCase().includes(search) ||
          item.prenom?.toLowerCase().includes(search) ||
          item.ville?.toLowerCase().includes(search)
      );
    }

    // Sort for full-list
    if (activeTab === "full-list") {
      result.sort((a, b) => {
        let valueA, valueB;
        
        switch (sortField) {
          case "nom":
            valueA = `${a.nom} ${a.prenom}`.toLowerCase();
            valueB = `${b.nom} ${b.prenom}`.toLowerCase();
            break;
          case "prenom":
            valueA = `${a.prenom} ${a.nom}`.toLowerCase();
            valueB = `${b.prenom} ${b.nom}`.toLowerCase();
            break;
          case "ville":
            valueA = (a.ville || "").toLowerCase();
            valueB = (b.ville || "").toLowerCase();
            break;
          case "code_client":
          default:
            valueA = a.code_client || "";
            valueB = b.code_client || "";
            break;
        }

        if (sortOrder === "asc") {
          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
        } else {
          return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
        }
      });
    }

    setFilteredData(result);
  };

  const fetchClientDetails = async (clientId) => {
    setDetailsLoading(true);
    setDetailsDialogOpen(true);

    try {
      const response = await axios.get(`${API}/status/${clientId}`, {
        withCredentials: true,
      });
      setClientStatus(response.data);
      setSelectedClient(response.data.client);
    } catch (error) {
      console.error("Error fetching client details:", error);
      toast.error("Erreur lors du chargement des détails");
    } finally {
      setDetailsLoading(false);
    }
  };

  const exportToCsv = () => {
    if (filteredData.length === 0) return;

    const headers = Object.keys(filteredData[0]);
    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            if (typeof value === "object" && value !== null) return JSON.stringify(value);
            if (value === null || value === undefined) return "";
            if (typeof value === "string" && value.includes(","))
              return `"${value}"`;
            return value;
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `rapport_${activeTab}_${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
    }
    return sortOrder === "asc" ? (
      <ArrowUp className="h-3 w-3 text-primary" />
    ) : (
      <ArrowDown className="h-3 w-3 text-primary" />
    );
  };

  const tabs = [
    {
      id: "full-list",
      label: "Liste complète",
      icon: List,
      description: "Tous les clients avec détails complets",
    },
    {
      id: "in-progress",
      label: "En cours",
      icon: Clock,
      description: "Dossiers reçus sans paiement ni signature",
    },
    {
      id: "paid-full",
      label: "Payés",
      icon: CreditCard,
      description: "Honoraires payés intégralement",
    },
    {
      id: "partial-payment",
      label: "Partiels",
      icon: AlertCircle,
      description: "Solde restant à payer",
    },
    {
      id: "signed-forms",
      label: "Signés",
      icon: FileText,
      description: "État des signatures",
    },
    {
      id: "completed",
      label: "Terminés",
      icon: CheckCircle2,
      description: "TED effectué",
    },
  ];

  const StatusBadge = ({ status, label }) => (
    <div className="flex items-center gap-1">
      <span className={`status-indicator ${status ? "green" : "red"}`} />
      <span className={`text-xs ${status ? "text-green-600" : "text-red-600"}`}>
        {label || (status ? "OUI" : "NON")}
      </span>
    </div>
  );

  const StatusRow = ({ label, status, icon: Icon, detail }) => (
    <div className="flex items-center justify-between p-3 border-b last:border-b-0">
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <div>
          <p className="font-medium text-foreground text-sm">{label}</p>
          {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
        </div>
      </div>
      <StatusBadge status={status} />
    </div>
  );

  const renderFullListTable = (isFullscreen = false) => {
    const tableData = isFullscreen ? filteredData : filteredData;
    
    return (
      <ScrollArea className="w-full">
        <table className={`data-table ${isFullscreen ? "min-w-full" : "min-w-[1400px]"}`} data-testid="report-table">
          <thead>
            <tr>
              <th className="pl-6 whitespace-nowrap">
                <button
                  onClick={() => toggleSort("code_client")}
                  className="flex items-center gap-1 hover:text-primary transition-colors"
                >
                  Code
                  <SortIcon field="code_client" />
                </button>
              </th>
              <th className="whitespace-nowrap">
                <button
                  onClick={() => toggleSort("nom")}
                  className="flex items-center gap-1 hover:text-primary transition-colors"
                >
                  Client
                  <SortIcon field="nom" />
                </button>
              </th>
              <th className="whitespace-nowrap">Conjoint(e)</th>
              <th className="text-right whitespace-nowrap">Honoraires</th>
              <th className="text-right whitespace-nowrap">Payé</th>
              <th className="text-right whitespace-nowrap">Solde</th>
              <th className="whitespace-nowrap">Paiement</th>
              <th className="whitespace-nowrap">Sign. Principal</th>
              <th className="whitespace-nowrap">Sign. Conjoint</th>
              <th className="whitespace-nowrap">TED Principal</th>
              <th className="whitespace-nowrap">TED Conjoint</th>
              <th className="whitespace-nowrap">Statut</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((item) => (
              <tr 
                key={item.client_id}
                className="cursor-pointer hover:bg-primary/5 transition-colors"
              >
                <td 
                  className="pl-6 font-mono text-sm font-medium text-primary whitespace-nowrap hover:underline"
                  onClick={() => fetchClientDetails(item.client_id)}
                >
                  {item.code_client}
                </td>
                <td 
                  className="whitespace-nowrap cursor-pointer hover:text-primary"
                  onClick={() => fetchClientDetails(item.client_id)}
                >
                  <div>
                    <p className="font-medium">{item.prenom} {item.nom}</p>
                    <p className="text-xs text-muted-foreground">{item.ville}</p>
                  </div>
                </td>
                <td className="whitespace-nowrap">
                  {item.has_spouse && item.conjoint_prenom ? (
                    <span>{item.conjoint_prenom} {item.conjoint_nom}</span>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </td>
                <td className="text-numeric whitespace-nowrap">
                  {item.honoraires > 0 ? `${item.honoraires.toFixed(2)} $` : "-"}
                </td>
                <td className="text-numeric whitespace-nowrap text-green-600">
                  {item.montant_paye > 0 ? `${item.montant_paye.toFixed(2)} $` : "-"}
                </td>
                <td className="text-numeric whitespace-nowrap">
                  {item.preparation_gratuite ? (
                    <span className="text-green-600 font-medium">Gratuit</span>
                  ) : item.solde > 0 ? (
                    <span className="text-red-600 font-medium">{item.solde.toFixed(2)} $</span>
                  ) : item.solde < 0 ? (
                    <span className="text-green-600 font-medium">{Math.abs(item.solde).toFixed(2)} $ (trop-perçu)</span>
                  ) : (
                    <span className="text-green-600">0.00 $</span>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  {item.preparation_gratuite ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle2 className="h-3 w-3" />
                      Gratuit
                    </span>
                  ) : (
                    <StatusBadge status={item.paiement_complet} />
                  )}
                </td>
                <td className="whitespace-nowrap">
                  <StatusBadge status={item.principal_signe} />
                </td>
                <td className="whitespace-nowrap">
                  {item.has_spouse && item.conjoint_residence !== "Non Résident" ? (
                    <StatusBadge status={item.conjoint_signe} />
                  ) : (
                    <span className="text-muted-foreground text-sm">N/A</span>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  <StatusBadge status={item.principal_transmis} />
                </td>
                <td className="whitespace-nowrap">
                  {item.has_spouse && item.conjoint_residence !== "Non Résident" ? (
                    <StatusBadge status={item.conjoint_transmis} />
                  ) : (
                    <span className="text-muted-foreground text-sm">N/A</span>
                  )}
                </td>
                <td className="whitespace-nowrap">
                  {item.dossier_complet ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <CheckCircle2 className="h-3 w-3" />
                      Complet
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                      <Clock className="h-3 w-3" />
                      En cours
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (filteredData.length === 0) {
      return (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground">Aucune donnée pour ce rapport</p>
        </div>
      );
    }

    switch (activeTab) {
      case "full-list":
        return renderFullListTable();

      case "in-progress":
        return (
          <table className="data-table" data-testid="report-table">
            <thead>
              <tr>
                <th className="pl-6">Code</th>
                <th>Nom complet</th>
                <th>Ville</th>
                <th>Situation</th>
                <th>Résidence</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item.client_id}>
                  <td className="pl-6 font-mono text-sm font-medium text-primary">
                    {item.code_client}
                  </td>
                  <td className="font-medium">
                    {item.prenom} {item.nom}
                  </td>
                  <td className="text-muted-foreground">{item.ville}</td>
                  <td>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-muted">
                      {item.situation_familiale}
                    </span>
                  </td>
                  <td className="text-sm text-muted-foreground">{item.residence}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case "paid-full":
        return (
          <table className="data-table" data-testid="report-table">
            <thead>
              <tr>
                <th className="pl-6">Code</th>
                <th>Nom complet</th>
                <th className="text-right">Honoraires</th>
                <th className="text-right">Payé</th>
                <th>Date paiement</th>
                <th>Mode</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item.client_id}>
                  <td className="pl-6 font-mono text-sm font-medium text-primary">
                    {item.code_client}
                  </td>
                  <td className="font-medium">
                    {item.prenom} {item.nom}
                  </td>
                  <td className="text-numeric">{item.total_fees?.toFixed(2)} $</td>
                  <td className="text-numeric text-green-600">
                    {item.total_paid?.toFixed(2)} $
                  </td>
                  <td className="font-mono text-sm">
                    {item.last_payment_date
                      ? format(new Date(item.last_payment_date), "dd/MM/yyyy")
                      : "-"}
                  </td>
                  <td className="text-muted-foreground">{item.payment_mode || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case "partial-payment":
        return (
          <table className="data-table" data-testid="report-table">
            <thead>
              <tr>
                <th className="pl-6">Code</th>
                <th>Nom complet</th>
                <th className="text-right">Honoraires</th>
                <th className="text-right">Payé</th>
                <th className="text-right">Solde</th>
                <th>Date avance</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item.client_id}>
                  <td className="pl-6 font-mono text-sm font-medium text-primary">
                    {item.code_client}
                  </td>
                  <td className="font-medium">
                    {item.prenom} {item.nom}
                  </td>
                  <td className="text-numeric">{item.total_fees?.toFixed(2)} $</td>
                  <td className="text-numeric">{item.total_paid?.toFixed(2)} $</td>
                  <td className="text-numeric text-red-600 font-medium">
                    {item.balance?.toFixed(2)} $
                  </td>
                  <td className="font-mono text-sm">
                    {item.last_payment_date
                      ? format(new Date(item.last_payment_date), "dd/MM/yyyy")
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case "signed-forms":
        return (
          <table className="data-table" data-testid="report-table">
            <thead>
              <tr>
                <th className="pl-6">Code</th>
                <th>Nom complet</th>
                <th>Situation</th>
                <th>Principal signé</th>
                <th>Conjoint(e) signé</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item.client_id}>
                  <td className="pl-6 font-mono text-sm font-medium text-primary">
                    {item.code_client}
                  </td>
                  <td className="font-medium">
                    {item.prenom} {item.nom}
                  </td>
                  <td>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-muted">
                      {item.situation_familiale}
                    </span>
                  </td>
                  <td>
                    <StatusBadge status={item.principal_signed} />
                  </td>
                  <td>
                    {item.spouse_signed !== null ? (
                      <StatusBadge status={item.spouse_signed} />
                    ) : (
                      <span className="text-muted-foreground text-sm">N/A</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      case "completed":
        return (
          <table className="data-table" data-testid="report-table">
            <thead>
              <tr>
                <th className="pl-6">Code</th>
                <th>Nom complet</th>
                <th>Situation</th>
                <th>TED principal</th>
                <th>TED conjoint(e)</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item.client_id}>
                  <td className="pl-6 font-mono text-sm font-medium text-primary">
                    {item.code_client}
                  </td>
                  <td className="font-medium">
                    {item.prenom} {item.nom}
                  </td>
                  <td>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-muted">
                      {item.situation_familiale}
                    </span>
                  </td>
                  <td className="font-mono text-sm">
                    {item.principal_transmission_date
                      ? format(
                          new Date(item.principal_transmission_date),
                          "dd/MM/yyyy"
                        )
                      : "-"}
                  </td>
                  <td className="font-mono text-sm">
                    {item.spouse_transmission_date
                      ? format(
                          new Date(item.spouse_transmission_date),
                          "dd/MM/yyyy"
                        )
                      : item.situation_familiale === "Marié(e)" ||
                        item.situation_familiale === "Conjoint(e) de fait"
                      ? "-"
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );

      default:
        return null;
    }
  };

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Éditions
            </h1>
            <p className="text-muted-foreground mt-1">
              Rapports et statistiques des dossiers
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => fetchReport(activeTab)}
              disabled={loading}
              data-testid="refresh-report-button"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
            {activeTab === "full-list" && (
              <Button
                variant="outline"
                onClick={() => setFullscreenOpen(true)}
                data-testid="fullscreen-button"
              >
                <Maximize2 className="h-4 w-4 mr-2" />
                Plein écran
              </Button>
            )}
            <Button
              onClick={exportToCsv}
              disabled={filteredData.length === 0 || loading}
              data-testid="export-button"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter CSV
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 h-auto p-1">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex flex-col items-center gap-1 py-3 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="text-xs font-medium hidden sm:block">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <tab.icon className="h-5 w-5 text-primary" />
                        {tab.label}
                      </CardTitle>
                      <CardDescription>
                        {tab.description} - {filteredData.length} résultat{filteredData.length !== 1 ? "s" : ""}
                      </CardDescription>
                    </div>
                  </div>
                  
                  {/* Search and Sort for full-list */}
                  {tab.id === "full-list" && (
                    <div className="flex flex-col sm:flex-row gap-4 mt-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Rechercher par code, nom, ville..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                          data-testid="search-input"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">Trier par:</span>
                        <Select value={sortField} onValueChange={setSortField}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="code_client">Code</SelectItem>
                            <SelectItem value="nom">Nom</SelectItem>
                            <SelectItem value="prenom">Prénom</SelectItem>
                            <SelectItem value="ville">Ville</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                        >
                          {sortOrder === "asc" ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : (
                            <ArrowDown className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">{renderTable()}</div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Fullscreen Dialog */}
        <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
          <DialogContent className="max-w-[95vw] w-full max-h-[95vh] h-full">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  <List className="h-5 w-5 text-primary" />
                  Liste complète des clients
                </DialogTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportToCsv}
                    disabled={filteredData.length === 0}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Exporter
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setFullscreenOpen(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Search and Sort in fullscreen */}
              <div className="flex flex-col sm:flex-row gap-4 mt-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par code, nom, ville..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Trier par:</span>
                  <Select value={sortField} onValueChange={setSortField}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="code_client">Code</SelectItem>
                      <SelectItem value="nom">Nom</SelectItem>
                      <SelectItem value="prenom">Prénom</SelectItem>
                      <SelectItem value="ville">Ville</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                  >
                    {sortOrder === "asc" ? (
                      <ArrowUp className="h-4 w-4" />
                    ) : (
                      <ArrowDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {filteredData.length} client{filteredData.length !== 1 ? "s" : ""} - Cliquez sur un code pour voir les détails
              </p>
            </DialogHeader>
            
            <div className="flex-1 overflow-auto mt-4">
              {renderFullListTable(true)}
            </div>
          </DialogContent>
        </Dialog>

        {/* Client Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-primary" />
                Détails du client - {selectedClient?.code_client}
              </DialogTitle>
            </DialogHeader>

            {detailsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : clientStatus ? (
              <div className="space-y-6">
                {/* Client Info */}
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-muted-foreground">Client:</span>
                      <p className="font-medium">
                        {clientStatus.client.prenom} {clientStatus.client.nom}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Situation:</span>
                      <p className="font-medium">{clientStatus.client.situation_familiale}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Ville:</span>
                      <p className="font-medium">{clientStatus.client.ville}</p>
                    </div>
                    {clientStatus.client.conjoint && (
                      <div>
                        <span className="text-sm text-muted-foreground">Conjoint(e):</span>
                        <p className="font-medium">
                          {clientStatus.client.conjoint.prenom} {clientStatus.client.conjoint.nom}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Status */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-primary" />
                    État financier
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg bg-muted/50 text-center">
                      <p className="text-xs text-muted-foreground">Honoraires</p>
                      <p className="font-mono font-medium text-lg">
                        {(clientStatus.payment.honoraires_convenus || 0).toFixed(2)} $
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-green-50 text-center">
                      <p className="text-xs text-green-600">Payé</p>
                      <p className="font-mono font-medium text-lg text-green-700">
                        {clientStatus.payment.total_paid.toFixed(2)} $
                      </p>
                      {clientStatus.payment.payment_date && (
                        <p className="text-xs text-green-600 mt-1">
                          {format(new Date(clientStatus.payment.payment_date), "dd/MM/yyyy", { locale: fr })}
                        </p>
                      )}
                    </div>
                    <div className={`p-3 rounded-lg text-center ${
                      clientStatus.payment.balance > 0 
                        ? "bg-red-50" 
                        : clientStatus.payment.balance < 0 
                          ? "bg-green-50" 
                          : "bg-green-50"
                    }`}>
                      <p className={`text-xs ${
                        clientStatus.payment.balance > 0 
                          ? "text-red-600" 
                          : "text-green-600"
                      }`}>
                        {clientStatus.payment.balance > 0 
                          ? "Reste à payer" 
                          : clientStatus.payment.balance < 0 
                            ? "Trop-perçu"
                            : "Solde"
                        }
                      </p>
                      <p className={`font-mono font-medium text-lg ${
                        clientStatus.payment.balance > 0 
                          ? "text-red-700" 
                          : "text-green-700"
                      }`}>
                        {Math.abs(clientStatus.payment.balance).toFixed(2)} $
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status Details */}
                <div className="divide-y rounded-lg border">
                  <StatusRow
                    label="Paiement"
                    status={clientStatus.payment.complete}
                    icon={CreditCard}
                    detail={
                      clientStatus.payment.complete
                        ? "Honoraires payés en totalité"
                        : `Solde: ${clientStatus.payment.balance.toFixed(2)} $`
                    }
                  />
                  <StatusRow
                    label="Formulaires signés - Requérant principal"
                    status={clientStatus.forms.principal_signed}
                    icon={FileText}
                  />
                  {clientStatus.forms.has_spouse && clientStatus.client.conjoint?.residence !== "Non Résident" && (
                    <StatusRow
                      label="Formulaires signés - Conjoint(e)"
                      status={clientStatus.forms.spouse_signed}
                      icon={FileText}
                    />
                  )}
                  <StatusRow
                    label="TED - Requérant principal"
                    status={clientStatus.transmission.principal_transmitted}
                    icon={Send}
                    detail={
                      clientStatus.transmission.principal_date
                        ? `Transmis le ${format(new Date(clientStatus.transmission.principal_date), "dd/MM/yyyy", { locale: fr })}`
                        : undefined
                    }
                  />
                  {clientStatus.forms.has_spouse && clientStatus.client.conjoint?.residence !== "Non Résident" && (
                    <StatusRow
                      label="TED - Conjoint(e)"
                      status={clientStatus.transmission.spouse_transmitted}
                      icon={Send}
                      detail={
                        clientStatus.transmission.spouse_date
                          ? `Transmis le ${format(new Date(clientStatus.transmission.spouse_date), "dd/MM/yyyy", { locale: fr })}`
                          : undefined
                      }
                    />
                  )}
                </div>

                {/* Overall Status */}
                <div>
                  {(() => {
                    const isSpouseNonResident = clientStatus.client.conjoint?.residence === "Non Résident";
                    const spouseSignedOrNA = !clientStatus.forms.has_spouse || isSpouseNonResident || clientStatus.forms.spouse_signed;
                    const spouseTransmittedOrNA = !clientStatus.forms.has_spouse || isSpouseNonResident || clientStatus.transmission.spouse_transmitted;
                    
                    const isComplete = clientStatus.payment.complete &&
                      clientStatus.forms.principal_signed &&
                      spouseSignedOrNA &&
                      clientStatus.transmission.principal_transmitted &&
                      spouseTransmittedOrNA;
                    
                    return isComplete ? (
                    <div className="p-4 rounded-lg bg-green-50 border border-green-200 flex items-center gap-3">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-700">Dossier complété</p>
                        <p className="text-sm text-green-600">
                          Toutes les étapes ont été complétées
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 flex items-center gap-3">
                      <Clock className="h-6 w-6 text-yellow-600" />
                      <div>
                        <p className="font-medium text-yellow-700">Dossier en cours</p>
                        <p className="text-sm text-yellow-600">
                          Certaines étapes restent à compléter
                        </p>
                      </div>
                    </div>
                  );
                  })()}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ReportsPage;
